import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useTranslation } from 'react-i18next';
import { colors } from 'components/styles/variables';
import { CreateEmailAccountParams } from 'types/Auth';
import { useValidation } from 'lib/hooks/useValidation';
import { buildLoginFormSchema } from 'lib/validationSchemas';
import Separator from './Separator';
import Header from './Header';
import SubmitButton from './SubmitButton';
import StageWrapper from './StageWrapper';
import { formCss, errorCss, inputCss, checkboxCss } from './styles';

interface Props {
  onSubmit(values: CreateEmailAccountParams): void | Promise<void>;
}

export default function CreateAccountByEmail({ onSubmit }: Props): JSX.Element {
  const { t } = useTranslation();
  const { validateEmail } = useValidation();

  const schema = buildLoginFormSchema({ emailValidator: validateEmail });

  return (
    <StageWrapper>
      <Header>
        <h1>{t('auth.createAccount.headline')}</h1>
        <Separator />
      </Header>
      <Formik
        initialValues={{ email: '', password: '', acceptEmails: true }}
        validationSchema={schema}
        onSubmit={async (values, { setSubmitting }): Promise<void> => {
          await onSubmit(values);

          setSubmitting(false);
        }}
      >
        {({ isSubmitting }): JSX.Element => (
          <Form className={formCss.className} data-testid="email-account-creation-form">
            <label>{t('auth.common.email')}</label>
            <Field
              type="email"
              name="email"
              placeholder="email@email.com"
              className={inputCss.className}
              autoComplete="on"
            />
            <div className={`error ${errorCss.className}`}>
              <ErrorMessage name="email">
                {(error): string => {
                  return t(error);
                }}
              </ErrorMessage>
            </div>
            <label>{t('auth.common.password')}</label>
            <Field
              type="password"
              name="password"
              className={inputCss.className}
              autoComplete="on"
            />
            <div className={`error ${errorCss.className}`}>
              <ErrorMessage name="password">
                {(error): string => {
                  return t(error);
                }}
              </ErrorMessage>
            </div>

            <SubmitButton isSubmitting={isSubmitting}>
              {t('auth.createAccount.createButton')}
            </SubmitButton>
            <div className={`accept-emails-container ${checkboxCss.className}`}>
              <Field type="checkbox" name="acceptEmails" className={checkboxCss.className} />
              <span className={`checkmark ${checkboxCss.className}`} />
              <span className={`accept-emails ${checkboxCss.className}`}>
                {t('auth.createAccount.acceptEmails')}
              </span>
            </div>
          </Form>
        )}
      </Formik>
      {formCss.styles}
      {inputCss.styles}
      {errorCss.styles}
      {checkboxCss.styles}
      <style jsx>{`
        label {
          color: ${colors.BLACK};
          font-size: 13px;
          font-weight: 600;
          height: 17px;
          text-align: left;
          text-transform: uppercase;
        }
      `}</style>
    </StageWrapper>
  );
}
